import { useCommercePermissionAccess } from '@/hooks/use-commerce-permission-access';
import { useClientPermissionAccess } from '@/hooks/use-client-permission-access';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from '@tanstack/react-query';
import { Gift } from 'lucide-react';
import toast from 'react-hot-toast';
import { useDebounce } from '@/hooks/use-debounce';
import { keepPreviousDataWhen } from '@/hooks/use-query-loading-state';
import { PageCanvas, SplitLayout, SidePanelPlaceholder } from '@/components/layout';
import {
  useGetGiftCards,
  useGetGiftCardsId,
  usePostGiftCards,
  usePatchGiftCardsId,
  getGetGiftCardsQueryKey,
  getGetGiftCardsIdQueryKey,
  getGetGiftCardsIdQueryOptions,
} from '@/api/generated/gift-cards/gift-cards';
import type {
  GetGiftCards200DataItemsItem,
  PostGiftCardsBody,
  PatchGiftCardsIdBody,
} from '@/api/generated/schemas';
import { GiftCardsList } from '@/components/gift-cards/gift-cards-list';
import { GiftCardDetail } from '@/components/gift-cards/gift-card-detail';
import { GiftCardDetailSkeleton } from '@/components/gift-cards/gift-card-detail-skeleton';
import { GiftCardForm, type GiftCardFormValues } from '@/components/gift-cards/gift-card-form';
import { dollarsToCents, centsToDollars } from '@/components/gift-cards/gift-card-format';
import { formatDocumentTitle } from '@/lib/document-title';
import { getApiErrorMessage } from '@/lib/error-utils';

type GiftCardsSearchParams = {
  gc?: string;
};

export const Route = createFileRoute('/_auth/$orgId/gift-cards')({
  validateSearch: (search: Record<string, unknown>): GiftCardsSearchParams => ({
    gc: typeof search.gc === 'string' ? search.gc : undefined,
  }),
  component: GiftCardsPage,
});

/** Side panel modes for the gift cards master-detail page. */
type PanelMode = 'empty' | 'new' | 'detail' | 'edit';

function SidePanelCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full bg-clovie-canvas p-3 lg:p-0">
      <div className="h-full overflow-hidden rounded-[var(--r-2xl)] border border-clovie-border bg-clovie-surface shadow-soft-lg lg:rounded-none lg:border-0 lg:shadow-none">
        {children}
      </div>
    </div>
  );
}

function GiftCardsPage() {
  const { scopeKey } = useClientPermissionAccess();
  const access = useCommercePermissionAccess();
  if (!access.canManageGiftCards) return <p className="p-6 text-clovie-muted">You do not have access to gift cards.</p>;
  return <ScopedGiftCardsPage key={scopeKey} />;
}

function ScopedGiftCardsPage() {
  const privacy = useClientPermissionAccess();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const commerce = useCommercePermissionAccess();
  const canWriteOperational = commerce.canManageGiftCards;
  const { orgId } = useParams({ from: '/_auth/$orgId/gift-cards' });
  const { gc } = Route.useSearch();
  const initialGiftCardSearch = gc?.trim();
  const autoSelectedGiftCardSearchRef = useRef<string | null>(null);

  const [panelMode, setPanelMode] = useState<PanelMode>('empty');
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [search, setSearch] = useState(initialGiftCardSearch ?? '');
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    if (!initialGiftCardSearch) return;

    setSearch(initialGiftCardSearch);
  }, [initialGiftCardSearch]);

  // List query
  const {
    data: listResponse,
    isLoading: isListLoading,
    isPlaceholderData: isListRefining,
    error: listError,
  } = useGetGiftCards(
    { search: debouncedSearch || undefined },
    {
      query: {
        queryKey: [...getGetGiftCardsQueryKey({ search: debouncedSearch || undefined }), privacy.scopeKey],
        // Typing a search keeps the current rows (dimmed) — never across a salon/privacy scope.
        placeholderData: keepPreviousDataWhen((previousKey) => previousKey.at(-1) === privacy.scopeKey),
      },
    }
  );
  const giftCards = useMemo(() => listResponse?.data?.items ?? [], [listResponse?.data?.items]);

  useEffect(() => {
    if (!initialGiftCardSearch) {
      autoSelectedGiftCardSearchRef.current = null;
      return;
    }

    if (autoSelectedGiftCardSearchRef.current === initialGiftCardSearch) return;

    const matchingCard = giftCards.find((card) => card.number === initialGiftCardSearch);
    if (!matchingCard) return;

    setSelectedCardId(matchingCard.id);
    setPanelMode('detail');
    autoSelectedGiftCardSearchRef.current = initialGiftCardSearch;
  }, [giftCards, initialGiftCardSearch]);

  // Detail query — lazy: only fetches the selected card. staleTime keeps a
  // recently-viewed card instant on re-open; hover prefetch warms it earlier.
  const { data: detailResponse, isLoading: isDetailLoading, isError: isDetailError } = useGetGiftCardsId(
    selectedCardId ?? '',
    { query: { enabled: !!selectedCardId, staleTime: 60_000, queryKey: [...getGetGiftCardsIdQueryKey(selectedCardId ?? ''), privacy.scopeKey] } }
  );
  const selectedCard = detailResponse?.data ?? null;

  // Prefetch a card's detail on row hover/focus so the click feels instant.
  const handleRowHover = useCallback(
    (card: GetGiftCards200DataItemsItem) => {
      queryClient.prefetchQuery(
        getGetGiftCardsIdQueryOptions(card.id, { query: { staleTime: 60_000, queryKey: [...getGetGiftCardsIdQueryKey(card.id), privacy.scopeKey] } })
      );
    },
    [queryClient, privacy.scopeKey]
  );

  // Mutations
  const createMutation = usePostGiftCards();
  const updateMutation = usePatchGiftCardsId();

  useEffect(() => {
    if (canWriteOperational || (panelMode !== 'new' && panelMode !== 'edit')) return;
    setPanelMode(selectedCardId ? 'detail' : 'empty');
  }, [canWriteOperational, panelMode, selectedCardId]);

  const invalidateList = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getGetGiftCardsQueryKey() });
  }, [queryClient]);

  // List interactions
  const handleAdd = useCallback(() => {
    if (!canWriteOperational) return;
    setSelectedCardId(null);
    setPanelMode('new');
  }, [canWriteOperational]);

  const handleRowClick = useCallback((card: GetGiftCards200DataItemsItem) => {
    setSelectedCardId(card.id);
    setPanelMode('detail');
  }, []);

  // Phones/tablets (single-pane): Back returns to the list.
  const handleMobileBack = useCallback(() => {
    setSelectedCardId(null);
    setPanelMode('empty');
  }, []);

  // Open the standalone Gift Card Settings page.
  const handleSettings = useCallback(() => {
    navigate({ to: '/$orgId/gift-cards/settings', params: { orgId } });
  }, [navigate, orgId]);

  // Create
  const handleCreate = useCallback(
    (values: GiftCardFormValues) => {
      if (!canWriteOperational) return;
      const body: PostGiftCardsBody = {
        number: values.number,
        balanceCents: dollarsToCents(values.balanceDollars),
      };
      if (values.ownerClient) body.ownerClientId = values.ownerClient.id;
      if (values.notes) body.notes = values.notes;

      createMutation.mutate(
        { data: body },
        {
          onSuccess: (res) => {
            const newId = res.data?.id;
            invalidateList();
            if (newId) {
              setSelectedCardId(newId);
              setPanelMode('detail');
            } else {
              setPanelMode('empty');
            }
            toast.success('Gift card created');
          },
          onError: (err) => {
            toast.error(getApiErrorMessage(err, 'Failed to create gift card'));
          },
        }
      );
    },
    [canWriteOperational, createMutation, invalidateList]
  );

  // Update
  const handleUpdate = useCallback(
    (values: GiftCardFormValues) => {
      if (!canWriteOperational) return;
      if (!selectedCardId || !selectedCard) return;
      const body: PatchGiftCardsIdBody = {
        number: values.number,
        balanceCents: dollarsToCents(values.balanceDollars),
        ...((values.ownerClient?.id ?? null) !== (selectedCard.ownerClient?.id ?? null)
          ? { ownerClientId: values.ownerClient?.id ?? null }
          : {}),
        ...(values.notes !== undefined ? { notes: values.notes || null } : {}),
      };

      updateMutation.mutate(
        { id: selectedCardId, data: body },
        {
          onSuccess: () => {
            invalidateList();
            queryClient.invalidateQueries({
              queryKey: getGetGiftCardsIdQueryKey(selectedCardId),
            });
            setPanelMode('detail');
            toast.success('Gift card updated');
          },
          onError: (err) => {
            toast.error(getApiErrorMessage(err, 'Failed to update gift card'));
          },
        }
      );
    },
    [canWriteOperational, selectedCardId, selectedCard, updateMutation, invalidateList, queryClient]
  );

  // Edit form initial values from the loaded detail
  const editInitial = useMemo<GiftCardFormValues | undefined>(() => {
    if (!selectedCard) return undefined;
    return {
      number: selectedCard.number,
      balanceDollars: centsToDollars(selectedCard.balanceCents),
      ownerClient: selectedCard.ownerClient
        ? {
            id: selectedCard.ownerClient.id,
            name: `${selectedCard.ownerClient.firstName} ${selectedCard.ownerClient.lastName ?? ''}`.trim(),
          }
        : null,
      notes: selectedCard.notes ?? '',
    };
  }, [selectedCard]);

  // Build side panel
  let sidePanel: React.ReactNode;
  if (canWriteOperational && panelMode === 'new') {
    sidePanel = (
      <GiftCardForm
        mode="create"
        isSubmitting={createMutation.isPending}
        onSubmit={handleCreate}
        onCancel={() => setPanelMode(selectedCardId ? 'detail' : 'empty')}
      />
    );
  } else if (canWriteOperational && panelMode === 'edit' && selectedCard && editInitial) {
    sidePanel = (
      <GiftCardForm
        key={selectedCard.id}
        mode="edit"
        initial={editInitial}
        isSubmitting={updateMutation.isPending}
        onSubmit={handleUpdate}
        onCancel={() => setPanelMode('detail')}
      />
    );
  } else if (panelMode === 'detail' && selectedCard) {
    sidePanel = (
      <GiftCardDetail
        giftCard={selectedCard}
        onEdit={canWriteOperational ? () => setPanelMode('edit') : undefined}
      />
    );
  } else if (panelMode === 'detail' && selectedCardId && isDetailLoading) {
    // Card clicked but detail still loading (cold cache) — show skeleton so the
    // panel doesn't fall back to the empty placeholder mid-transition.
    sidePanel = <GiftCardDetailSkeleton />;
  } else if (panelMode === 'detail' && selectedCardId && isDetailError) {
    sidePanel = (
      <SidePanelPlaceholder title="Couldn't load this gift card." description="Please try again.">
        <Gift className="mb-4 h-12 w-12 text-clovie-border-strong" />
      </SidePanelPlaceholder>
    );
  } else {
    sidePanel = (
      <SidePanelPlaceholder
        title={canWriteOperational ? 'Add a new gift card or select' : 'Select a gift card'}
        description={
          canWriteOperational
            ? 'an existing card to view or edit.'
            : 'an existing card to view details.'
        }
      >
        <Gift className="mb-4 h-12 w-12 text-clovie-border-strong" />
      </SidePanelPlaceholder>
    );
  }

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Gift Cards')}</title>
      </Helmet>
      <SplitLayout
        sidePanel={<SidePanelCard>{sidePanel}</SidePanelCard>}
        sidePanelWidth="md"
        mobileDetailOpen={panelMode !== 'empty'}
        onMobileBack={handleMobileBack}
        mobileBackLabel="Gift cards"
      >
        <PageCanvas className="h-full overflow-auto">
          <div className="min-h-full overflow-hidden rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface shadow-soft-lg">
            <GiftCardsList
              giftCards={giftCards}
              selectedCardId={selectedCardId}
              search={search}
              onSearchChange={setSearch}
              onAdd={handleAdd}
              readOnly={!canWriteOperational}
              onSettings={commerce.canManageGiftCardSettings ? handleSettings : undefined}
              onRowClick={handleRowClick}
              onRowHover={handleRowHover}
              isLoading={isListLoading}
              isRefining={isListRefining || search !== debouncedSearch}
              error={listError}
            />
          </div>
        </PageCanvas>
      </SplitLayout>
    </>
  );
}
